"use client";
import { useSearchParams, useRouter, usePathname } from "next/navigation";

const defaultParameters: Record<string, number | string> = {
  app: "",
  folderType: "all",
  lastRun: "",
  memberId: "",
  owner: "",
  page: 1,
  search: "",
  shared: "all",
  status: "all",
  view: "connections",
};

const stripDefaultParameters = (parameters: URLSearchParams) => {
  // If the param value matches the default value specified in defaultParams above,
  // remove it from the URL object
  for (const key of Object.keys(defaultParameters)) {
    if (
      parameters.has(key) &&
      parameters.get(key) === String(defaultParameters[key])
    ) {
      parameters.delete(key);
    }
  }
  return parameters;
};

/**
 * This hook is used to get the current URL search params and push new params to the URL.
 * It also strips out the default params from the URL.
 * @returns An object with the current URL search params and a function to push new params to the URL.
 */

export default function useRouterParams() {
  const parameters = useSearchParams();
  const { push } = useRouter();
  const pathname = usePathname();
  // Convert the search params to a string
  const queryString = parameters?.toString();
  const fullUrl = `${pathname}${queryString ? "?" + queryString : ""}`;

  const setParameters = (
    parametersToSet: Record<string, string | number>,
    omit?: string[],
    pathname?: string,
    forceSet?: boolean,
  ) => {
    // Make a copy of the readonly URLSearchParams object
    // so we can update params at the end of this function.
    // This will also preserve all current params
    const newParameters = new URLSearchParams(parameters?.toString());

    // set all new params
    for (const key of Object.keys(parametersToSet)) {
      if (parametersToSet[key] !== undefined) {
        newParameters.set(key, parametersToSet[key].toString());
      }
    }

    // always omit these params when any filter changes
    const _omit = [...(omit || []), "details", "managedAuthViolation"];
    // remove all omitted params
    for (const key of _omit) {
      newParameters.delete(key);
    }

    const strippedParameters = stripDefaultParameters(newParameters);

    const hash = globalThis?.location?.hash;

    // If the params have changed, make a new entry into route history
    // Force set to ignore the check and always update the URL
    if (parameters?.toString() !== strippedParameters.toString() || forceSet) {
      // If pathname is included in the function call, update the URL with the new params
      if (pathname) {
        push(`${pathname}?${strippedParameters.toString()}${hash}`, {
          scroll: false,
        });
        return;
      }
      push(`?${strippedParameters.toString()}${hash}`, { scroll: false });
    }
  };

  const app = parameters?.get("app");
  const apps = parameters?.get("apps");
  const appsOperator = parameters?.get("appsOperator");
  const connection = parameters?.get("connection");
  const connections = parameters?.get("connections");
  const connectionsOperator = parameters?.get("connectionsOperator");
  const folderType = parameters?.get("folderType");
  const lastRun = parameters?.get("lastRun");
  const memberId = parameters?.get("memberId");
  const managedAuthViolation = parameters?.get("managedAuthViolation");
  const order = parameters?.get("order");
  const owner = parameters?.get("owner");
  const owners = parameters?.get("owners");
  const ownersOperator = parameters?.get("ownersOperator");
  const page =
    (parameters?.get("page") && Number(parameters?.get("page"))) || null;
  const search = parameters?.get("search");
  const shared = parameters?.get("shared");
  const status = parameters?.get("status");
  const ownerStatus = parameters?.get("ownerStatus");
  const virtual = parameters?.get("virtual");
  const details = parameters?.get("details");
  const modal = parameters?.get("modal");
  const view = parameters?.get("view");
  const zapId = parameters?.get("zap_id");

  const ASSET_FILTER_PARAMS = [
    "app",
    "apps",
    "appsOperator",
    "connection",
    "connections",
    "connectionsOperator",
    "lastRun",
    "managedAuthViolation",
    "owner",
    "owners",
    "ownersOperator",
    "ownerStatus",
    "page",
    "search",
    "status",
  ];

  const OMITTED_FOLDER_QUERY_PARAMS = [
    "app",
    "connection",
    "status",
    "shared",
    "page",
    "virtual",
  ];

  const OMITTED_ZAP_QUERY_PARAMS = ["folderType", "page", "virtual"];

  // Clears all filter params
  const clearFilterParameters = () => {
    setParameters({}, ASSET_FILTER_PARAMS);
  };

  const clearAllParameters = () => {
    setParameters({}, [
      "app",
      "apps",
      "appsOperator",
      "connection",
      "connections",
      "connectionsOperator",
      "folderType",
      "lastRun",
      "managedAuthViolation",
      "memberId",
      "order",
      "owner",
      "owners",
      "ownersOperator",
      "ownerStatus",
      "page",
      "search",
      "shared",
      "status",
      "virtual",
      "details",
    ]);
  };

  return {
    app,
    apps,
    appsOperator,
    clearAllParams: clearAllParameters,
    clearFilterParams: clearFilterParameters,
    connection,
    connections,
    connectionsOperator,
    details,
    folderType,
    fullUrl,
    lastRun,
    memberId,
    modal,
    OMITTED_FOLDER_QUERY_PARAMS,
    OMITTED_ZAP_QUERY_PARAMS,
    managedAuthViolation,
    order,
    owner,
    owners,
    ownersOperator,
    ownerStatus,
    page,
    search,
    setParams: setParameters,
    shared,
    status,
    view,
    virtual,
    zapId,
  };
}
